import React, { useEffect, useState } from "react"
import { List, Typography } from "antd"
import { Link } from "react-router-dom" // adjust the import if needed

const { Title, Text } = Typography

const blockstyle = {
  background: "white",
  borderRadius: 15,
  padding: 25,
  width: "100%",
}

/**
 * Props:
 * - invoices: array of invoice objects.
 * - loading: boolean for the loading state.
 * - selectedYear: the selected year.
 * - selectedMonth: the selected month (if applicable).
 * - mode: "monthly" or "yearly" (default "monthly")
 */
export default function TopClients({
  invoices,
  loading,
  selectedYear,
  selectedMonth,
  mode = "monthly",
}) {
  const [clientData, setClientData] = useState([])

  useEffect(() => {
    // Filter invoices based on selectedYear.
    // If mode is "monthly", also filter by selectedMonth.
    const filteredInvoices = invoices.filter((invoice) => {
      const invoiceDate = new Date(invoice.invoiceDate.seconds * 1000)
      if (invoiceDate.getFullYear() !== selectedYear) return false

      // Only filter by month if mode is "monthly"
      if (mode === "monthly" && selectedMonth !== "wholeYear") {
        // JavaScript months are 0-indexed.
        if (invoiceDate.getMonth() !== parseInt(selectedMonth, 10) - 1) return false
      }
      return true
    })

    // Aggregate revenue and order count per client.
    const clientMap = {}
    filteredInvoices.forEach((invoice) => {
      // Use the client name from the invoice, fallback to "Unknown".
      const clientName = invoice.client?.client || "Unknown"

      if (!clientMap[clientName]) {
        clientMap[clientName] = { clientName, revenue: 0, orderCount: 0 }
      }

      // Sum revenue from the invoice (using totalNetoSum).
      clientMap[clientName].revenue += invoice.totalData?.totalNetoSum || 0
      clientMap[clientName].orderCount += 1
    })

    // Convert the map to an array, sort by revenue descending, and take the top 5.
    const topClients = Object.values(clientMap)
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 15)

    setClientData(topClients)
  }, [invoices, selectedYear, selectedMonth, mode])

  // Set a title based on mode
  const titleText = mode === "monthly" ? "Top Clients (Monthly)" : "Top Clients (Yearly)"

  return (
    <div style={blockstyle}>
      <Title style={{ marginBottom: 0 }} level={4}>
        {titleText}
      </Title>
      <Text style={{ color: "lightgray" }}>Most valuable clients based on sales</Text>
      <List
        size="small"
        itemLayout="horizontal"
        loading={loading}
        dataSource={clientData}
        renderItem={(item) => (
          <List.Item
            extra={
              <Title style={{ color: "#237804" }} level={5}>
                {`+ €${Number(item.revenue).toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`}
              </Title>
            }>
            <List.Item.Meta
              title={<Link>{item.clientName}</Link>}
              description={`Orders: ${item.orderCount}`}
            />
          </List.Item>
        )}
      />
    </div>
  )
}
